import React, { useEffect, useRef } from 'react';
import { useRouter, usePathname, useGlobalSearchParams } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import {
  selectLastVisitedPath,
  selectLastVisitedParams,
  selectShouldRestoreNavigation,
  clearNavigationState,
} from '../redux/navigationSlice';
import { normalizeNavPath } from '../utils/navigationUtils';
import { decideRestore } from '../utils/navigationRestore';

interface NavigationRestorerProps {
  children: React.ReactNode;
  userId?: string;
}

export const NavigationRestorer: React.FC<NavigationRestorerProps> = ({ children, userId }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const currentPathname = usePathname();
  const currentParams = useGlobalSearchParams();
  const hasRestoredRef = useRef(false);
  const persistState = useSelector((state: any) => state._persist);
  const isAuthenticated = useSelector((state: RootState) => state.auth?.isAuthenticated ?? false);
  const isGuest = useSelector((state: RootState) => state.auth?.isGuest ?? false);

  const lastVisitedPath = useSelector(selectLastVisitedPath);
  const lastVisitedParams = useSelector(selectLastVisitedParams);
  const shouldRestore = useSelector(selectShouldRestoreNavigation);

  useEffect(() => {
    // Wait for Redux Persist rehydration
    if (!persistState?.rehydrated) return;

    // Only restore once per app session
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    // Don't restore if not authenticated
    if (!isAuthenticated && !isGuest) return;

    // Don't restore when the user is mid-cooking — the cooking screen is the
    // correct destination and NavigationRestorer would redirect back to the
    // recipe-detail page that was visited just before pressing Cook.
    if (normalizeNavPath(currentPathname).startsWith('/cooking-plan')) return;

    // The query comes off the address bar, not useLocalSearchParams: this
    // component is a layout, and those params are scoped to its own route
    // segment, so up here they are always empty. This effect runs exactly once,
    // so reading them would see nothing and restore straight over the deep link
    // it is meant to yield to.
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const currentQueryKeys = search
      ? [...new URLSearchParams(search).keys()]
      : Object.keys(currentParams);

    const decision = decideRestore({
      currentPathname,
      currentQueryKeys,
      lastVisitedPath,
      lastVisitedParams,
      shouldRestore,
    });

    if (decision.action === 'clear') {
      dispatch(clearNavigationState());
      return;
    }
    if (decision.action === 'stay') return;

    // Restoring without the params leaves the screen without the one thing that
    // tells it what to show.
    router.replace(
      (decision.params
        ? { pathname: decision.path, params: decision.params }
        : decision.path) as any,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistState?.rehydrated, shouldRestore, lastVisitedPath, lastVisitedParams, currentPathname, userId, isAuthenticated, isGuest, router]);

  return <>{children}</>;
};
